import { getConfig } from "./config";

const TOKEN_KEY = "otto_access_token";
const ID_TOKEN_KEY = "otto_id_token";
const REFRESH_TOKEN_KEY = "otto_refresh_token";
const PKCE_VERIFIER_KEY = "otto_pkce_verifier";
const POST_LOGIN_REDIRECT_KEY = "otto_post_login_redirect";

interface TokenSet {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

interface JwtPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  iss?: string;
  client_id?: string;
  aud?: string | string[];
  [key: string]: unknown;
}

function parseJwt(token: string): JwtPayload {
  const base64Url = token.split(".")[1];
  if (!base64Url) return {};
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
  return JSON.parse(json) as JwtPayload;
}

function buildPkceVerifier(): string {
  const verifier = Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("");
  localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  return verifier;
}

async function buildPkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function consumePkceVerifier(): string | null {
  const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  return verifier;
}

const TOKEN_STORAGE_KEYS = [TOKEN_KEY, ID_TOKEN_KEY, REFRESH_TOKEN_KEY] as const;

/**
 * Tokens live in localStorage (not sessionStorage) so a Slack / email deep
 * link opened in a fresh browser tab picks up the existing signed-in state
 * instead of forcing a fresh SSO round-trip in the new tab. sessionStorage
 * is per-tab; localStorage is shared across all tabs on the same origin.
 * Trade-off: tokens survive browser restart until their exp (access: 8h,
 * refresh: 30d). Acceptable for an internal ops dashboard behind Cognito
 * hosted UI + Entra SSO with token rotation.
 */
function readToken(key: string): string | null {
  return localStorage.getItem(key);
}

function writeToken(key: string, value: string): void {
  localStorage.setItem(key, value);
}

function clearAllTokens(): void {
  for (const key of TOKEN_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

/**
 * One-time migration: users who signed in before the sessionStorage ->
 * localStorage move keep valid tokens in their sessionStorage. Salvage
 * them on first module load so those users don't have to re-authenticate.
 * Runs at module scope, before any auth check. Safe to remove after ~30
 * days (max refresh-token TTL) when no lingering sessionStorage sessions
 * remain.
 */
function migrateTokensFromSessionStorage(): void {
  try {
    for (const key of TOKEN_STORAGE_KEYS) {
      if (localStorage.getItem(key)) continue;
      const legacy = sessionStorage.getItem(key);
      if (legacy) {
        localStorage.setItem(key, legacy);
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    /* storage unavailable - user will sign in fresh */
  }
}

migrateTokensFromSessionStorage();

const REDIRECT_PATH_PATTERN = /^\/(?!\/)[^\s]*$/;

/**
 * Stash the page the user was trying to reach so we can return them to it
 * after a fresh SSO round-trip. Called by ProtectedRoute when redirecting
 * unauthenticated users to /login, and by startSignIn() so a user who clicks
 * 'Continue with SSO' from a specific page also lands back on that page.
 *
 * Only stores same-origin relative paths (must start with '/' and not '//')
 * to avoid becoming an open redirect vector - a malicious link like
 * `.../login?redirect=https://evil.com` shouldn't ferry the user off-site
 * post-auth. Skips /login and /oauth/callback themselves since redirecting
 * back to those creates a loop.
 */
export function capturePostLoginRedirect(pathAndSearch: string): void {
  if (!REDIRECT_PATH_PATTERN.test(pathAndSearch)) return;
  if (pathAndSearch === "/login" || pathAndSearch.startsWith("/login?")) return;
  if (pathAndSearch === "/oauth/callback" || pathAndSearch.startsWith("/oauth/callback?")) return;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, pathAndSearch);
  } catch {
    /* sessionStorage unavailable - deep-link will still work if user manually navigates after sign-in */
  }
}

export function consumePostLoginRedirect(): string | null {
  try {
    const value = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Defensive guard against stale tokens surviving a Cognito app-client swap.
 *
 * When we point the dashboard at a different Cognito app client (e.g. the
 * production -> otto-reporting-import switch that shipped in July 2026),
 * users who had signed in against the OLD client keep valid-looking tokens
 * in storage. Their access token still parses, still has an unexpired exp,
 * so isAuthenticated() returns true - but every API call gets 401 because
 * the JWT is signed by the old pool, and every refresh attempt fails
 * because the refresh_token targets the old pool too. The user ends up in
 * a redirect loop (or, worse, gets sent to a Cognito hosted-UI URL that no
 * longer exists - AADSTS700016 flavour).
 *
 * We compare the token's `client_id` (access tokens) or `aud` (ID tokens)
 * claim to the current config's clientId. If they don't match we're
 * holding tokens from a previous configuration - drop them silently so
 * the normal not-signed-in flow kicks in and redirects to fresh SSO.
 * Runs once at module load (before any auth check).
 */
function purgeStaleTokensFromDifferentPool(): void {
  const config = getConfig();
  if (config.environment === "local") return;
  const expectedClientId = config.auth.clientId;
  if (!expectedClientId) return;

  const accessToken = readToken(TOKEN_KEY);
  if (!accessToken) return;

  try {
    const payload = parseJwt(accessToken);
    const tokenClientId =
      typeof payload.client_id === "string"
        ? payload.client_id
        : Array.isArray(payload.aud)
          ? payload.aud[0]
          : typeof payload.aud === "string"
            ? payload.aud
            : undefined;
    if (tokenClientId && tokenClientId !== expectedClientId) {
      console.info("[auth] purging stale tokens from previous Cognito client", {
        tokenClientId,
        expectedClientId,
      });
      clearAllTokens();
    }
  } catch {
    // Unparseable token - safest to clear it since the auth flow can't
    // do anything useful with garbage anyway.
    clearAllTokens();
  }
}

purgeStaleTokensFromDifferentPool();

export async function startSignIn(): Promise<void> {
  const config = getConfig();
  const redirectUri = `${window.location.origin}/oauth/callback`;
  capturePostLoginRedirect(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  const verifier = buildPkceVerifier();
  const challenge = await buildPkceChallenge(verifier);

  const params = new URLSearchParams({
    response_type: config.auth.responseType,
    client_id: config.auth.clientId,
    redirect_uri: redirectUri,
    scope: config.auth.scope,
    identity_provider: config.auth.identityProvider,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });

  window.location.assign(
    `${config.auth.cognitoHostedUiBaseUrl}/oauth2/authorize?${params.toString()}`,
  );
}

export function getLogoutUrl(): string {
  const config = getConfig();
  const logoutUri = window.location.origin + "/";
  const params = new URLSearchParams({
    client_id: config.auth.clientId,
    logout_uri: logoutUri,
  });
  return `${config.auth.cognitoHostedUiBaseUrl}/logout?${params.toString()}`;
}

export async function exchangeCodeForTokens(code: string): Promise<void> {
  const config = getConfig();
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const verifier = consumePkceVerifier();

  const bodyParams: Record<string, string> = {
    grant_type: "authorization_code",
    client_id: config.auth.clientId,
    redirect_uri: redirectUri,
    code,
  };

  if (verifier) {
    bodyParams.code_verifier = verifier;
  }

  const body = new URLSearchParams(bodyParams);

  const response = await fetch(`${config.auth.cognitoHostedUiBaseUrl}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const tokens = (await response.json()) as TokenSet;
  writeToken(TOKEN_KEY, tokens.access_token);
  writeToken(ID_TOKEN_KEY, tokens.id_token);
  if (tokens.refresh_token) {
    writeToken(REFRESH_TOKEN_KEY, tokens.refresh_token);
  }
}

export function getAccessToken(): string | null {
  return readToken(TOKEN_KEY);
}

export function getIdToken(): string | null {
  return readToken(ID_TOKEN_KEY);
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = parseJwt(token);
    if (!payload.exp) return true;
    return payload.exp * 1000 <= Date.now() + 60_000;
  } catch {
    return true;
  }
}

let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = readToken(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    const config = getConfig();
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.auth.clientId,
        refresh_token: refreshToken,
      });

      const response = await fetch(`${config.auth.cognitoHostedUiBaseUrl}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!response.ok) return false;

      const tokens = (await response.json()) as TokenSet;
      writeToken(TOKEN_KEY, tokens.access_token);
      writeToken(ID_TOKEN_KEY, tokens.id_token);
      if (tokens.refresh_token) {
        writeToken(REFRESH_TOKEN_KEY, tokens.refresh_token);
      }
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function getValidAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) return null;

  if (!isTokenExpired(token)) return token;

  const refreshed = await refreshAccessToken();
  return refreshed ? getAccessToken() : null;
}

export function isAuthenticated(): boolean {
  if (getConfig().environment === "local") return true;

  const token = getAccessToken();
  if (!token) return false;

  try {
    const payload = parseJwt(token);
    if (!payload.exp) return false;
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export interface UserInfo {
  email: string;
  givenName: string;
  familyName: string;
  sub: string;
}

export function getUserInfo(): UserInfo | null {
  if (getConfig().environment === "local") {
    return { email: "dev@local", givenName: "Local", familyName: "Dev", sub: "local" };
  }

  const token = getIdToken();
  if (!token) return null;

  try {
    const payload = parseJwt(token);
    return {
      email: (payload.email as string) ?? "",
      givenName: (payload.given_name as string) ?? "",
      familyName: (payload.family_name as string) ?? "",
      sub: (payload.sub as string) ?? "",
    };
  } catch {
    return null;
  }
}

const PLATFORM_ADMIN_GROUP = "platform-admin";

function parseGroupsClaim(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  }
  if (typeof value !== "string" || !value.trim()) return [];
  const trimmed = value.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter((item): item is string => typeof item === "string" && item.length > 0);
      }
    } catch {
      // Not valid JSON (e.g. '[a b]'); fall through to delimiter splitting.
    }
  }
  return trimmed
    .replace(/^\[|\]$/g, "")
    .split(/[\s,]+/)
    .map((group) => group.replace(/^"|"$/g, ""))
    .filter((group) => group.length > 0);
}

export function getUserGroups(): string[] {
  if (getConfig().environment === "local") return [PLATFORM_ADMIN_GROUP];

  const token = getIdToken();
  if (!token) return [];

  try {
    return parseGroupsClaim(parseJwt(token)["cognito:groups"]);
  } catch {
    return [];
  }
}

// UX only (nav visibility, friendly message); the API enforces admin access with a 403.
export function isPlatformAdmin(): boolean {
  return getUserGroups().includes(PLATFORM_ADMIN_GROUP);
}

export function logout(): void {
  clearAllTokens();
  window.location.href = getLogoutUrl();
}
